import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Play, Radio } from "lucide-react";

export default function YouTubeGallery({ limit = 8, showTitle = true }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/youtube/videos").then((r) => {
      setVideos(r.data.videos || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const displayed = videos.slice(0, limit);

  return (
    <section className="bg-halo-navy text-halo-cream py-20 px-6 md:px-12 lg:px-16" data-testid="youtube-section">
      {showTitle && (
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-halo-red font-sports uppercase tracking-[0.3em] text-xs mb-3">On YouTube</div>
            <h2 className="font-sports uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              Latest <span className="text-halo-red">Episodes</span>
            </h2>
          </div>
          <a
            data-testid="youtube-subscribe-btn"
            href="https://www.youtube.com/@halosintheinfield"
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-halo-red hover:bg-halo-redHover px-5 py-3 font-sports uppercase tracking-wider text-xs"
          >
            Subscribe on YouTube
          </a>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="aspect-video bg-halo-navyLight animate-pulse" />
          ))}
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-12 border border-halo-navyLight">
          <p className="text-halo-cream/60 font-body">
            No videos loaded yet. Check back soon — fresh content drops weekly.
          </p>
          <a href="https://www.youtube.com/@halosintheinfield" target="_blank" rel="noreferrer"
             className="inline-block mt-4 font-sports uppercase tracking-wider text-halo-red text-sm">
            Visit YouTube Channel →
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {displayed.map((v) => (
            <a
              key={v.id} href={v.url} target="_blank" rel="noreferrer"
              data-testid={`youtube-video-${v.id}`}
              className="group block bg-halo-navyLight border border-halo-navyLight hover:border-halo-red transition-colors"
            >
              <div className="aspect-video overflow-hidden relative">
                <img
                  src={v.thumbnail} alt={v.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-halo-navy/80 via-transparent to-transparent" />
                {v.is_live && (
                  <span className="absolute top-3 left-3 bg-halo-red text-white font-sports uppercase tracking-wider text-[10px] px-2 py-1 flex items-center gap-1">
                    <Radio className="w-3 h-3 animate-pulse-live" /> Live
                  </span>
                )}
                <div className="absolute bottom-3 right-3 w-10 h-10 bg-halo-red flex items-center justify-center opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-transform">
                  <Play className="w-4 h-4 fill-white text-white" />
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-sports uppercase tracking-tight text-base leading-tight line-clamp-2 group-hover:text-halo-red transition-colors">
                  {v.title}
                </h3>
                {v.published && (
                  <p className="text-xs text-halo-cream/50 font-body mt-2">
                    {new Date(v.published).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
